import { cn, formatCurrency } from "@/lib/utils";
import { AuctionState, Lot } from "@prisma/client";
import { Flex, Text } from "@radix-ui/themes";
import classNames from "classnames";
import { LotLiveData } from "../api/auctions/[auctionId]/gridData/route";
import LotIdentifierInfo from "@/app/components/LotIdentifierInfo";
import React, { useEffect, useState } from "react";
import { isAfter } from "date-fns";

interface Props {
  lot: Lot;
  auctionState: AuctionState;
  polledData: LotLiveData;
}

const FLASH_DURATION = 700;

const hasChanged = (
  prev: LotLiveData | undefined,
  current: LotLiveData | undefined
) => {
  if (!prev || !current) return prev !== current;
  // Current component is loading different data.
  if (prev.lotId !== current.lotId) return false;
  return prev?.highestBid !== current?.highestBid;
};

function LotValueInfo({ lot, auctionState, polledData }: Props) {
  const [flash, setFlash] = useState(false);
  const appraisalStyle =
    lot.appraisalMin === undefined ? "invisible" : "visible";

  useEffect(() => {
    if (flash) {
      setTimeout(() => {
        setFlash(false);
      }, FLASH_DURATION);
    }
  }, [flash]);

  const isInitialRender = React.useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (
      prevPolledData.current &&
      hasChanged(prevPolledData.current, polledData)
    ) {
      setFlash(true);
    }
    prevPolledData.current = polledData;
  }, [polledData]);

  const prevPolledData = React.useRef<LotLiveData | undefined>(undefined);
  return (
    <Flex justify="between" className="w-full bg-red-100u">
      <Flex direction="column">
        <Text
          className={classNames(
            "text-xs uppercase text-gray-400 xs-screen-text",
            appraisalStyle
          )}
        >
          Estimate
        </Text>
        <Text
          className={classNames(
            "text-sm -translate-y-0.5 sm-screen-text",
            appraisalStyle,
          )}
        >
          {formatCurrency(lot.appraisalMin!)} -{" "}
          {formatCurrency(lot.appraisalMax!)}
        </Text>
      </Flex>

      <Flex direction="column" align="end">
        <Text className={cn(
          "text-xs uppercase text-gray-400 mb-0 xs-screen-text",
        )}>
          {getText(polledData, auctionState)}
        </Text>
        <Flex
          justify="center"
          align="center"
          className={classNames(
            flash && "bg-gray-200",
            "transition-colors rounded-md w-fit px-1"
          )}
        >
          <Text className={cn(
            "text-sm font-semibold text-right -translate-y-0.5 sm-screen-text",
          )}>
            {getBidPrice(polledData, lot, auctionState)}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}

function getText(data: LotLiveData, auctionState: AuctionState) {
  if (auctionState === AuctionState.ARCHIVED) {
    return "Final Bid";
  }

  if (data.endDate && isAfter(new Date(data.endDate), new Date())) {
    if (data?.highestBid) return "Current Bid";
    else return "Start Bid";
  }
  return "Final Bid";
}

const getBidPrice = (
  polledData: LotLiveData | undefined,
  lot: Lot,
  auctionState: AuctionState
) => {
  if (auctionState === AuctionState.ARCHIVED) {
    return formatCurrency(lot.startBid!);
  } else if (auctionState === AuctionState.PREVIEW) {
    // Don't show ABFs during preview
    return formatCurrency(lot.startBid!);
  } else if (polledData?.highestBid) {
    return formatCurrency(polledData.highestBid);
  } else if (lot?.endDate && (new Date()).getTime() > (new Date(lot?.endDate)).getTime()) {
    // Not bid on
    return "-";
  } else {
    return formatCurrency(lot.startBid!);
  }
};

export default LotValueInfo;
